// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import axios from "axios";
import api, {
  getAccessToken,
  getRefreshToken,
  getRememberMe,
  setTokens,
  clearTokens,
} from "../api/axiosInstance";
import { API_ENDPOINTS } from "../constants/apiEndpoints";

/**
 * AuthContext에서 외부로 제공할 타입
 * - isAuthenticated: 로그인 여부
 * - login(access, refresh, rememberMe): 로그인 성공 시 토큰 저장 + 상태 업데이트
 * - logout(): 로그아웃 처리
 */
interface AuthContextType {
  isAuthenticated: boolean;
  login: (access: string, refresh: string, rememberMe?: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * API 베이스 URL (.env 기반)
 * - REACT_APP_API_BASE_URL이 없으면 http://localhost:8000 사용
 * - 마지막 슬래시는 제거 (http://localhost:8000/ -> http://localhost:8000)
 */
function getApiBase(): string {
  const v = process.env.REACT_APP_API_BASE_URL;
  return (v ?? "http://localhost:8000").replace(/\/+$/, "");
}

/**
 * AuthProvider
 * - 앱 전체를 감싸면서 로그인 상태를 제공
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // ✅ 앱 시작 시 localStorage의 access_token 기준으로 초기 로그인 여부 판정
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!getAccessToken();
  });

  const apiBase = useMemo(() => getApiBase(), []);

  useEffect(() => {
    const verifyToken = async () => {
      const token = getAccessToken();
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        await axios.post(
          `${apiBase}/api/token/verify/`,
          { token },
          { headers: { "Content-Type": "application/json" } }
        );
        setIsAuthenticated(true);
      } catch {
        // access 토큰 만료 → refresh로 갱신 시도
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          clearTokens();
          localStorage.removeItem("auth_user");
          sessionStorage.removeItem("auth_user");
          setIsAuthenticated(false);
          return;
        }

        try {
          const res = await axios.post(
            `${apiBase}/api/token/refresh/`,
            { refresh: refreshToken },
            { headers: { "Content-Type": "application/json" } }
          );
          const data = res.data as any;
          const newAccess: string = data.access;
          const newRefresh: string = data.refresh ?? refreshToken;
          setTokens(newAccess, newRefresh, getRememberMe());
          setIsAuthenticated(true);
        } catch {
          clearTokens();
          localStorage.removeItem("auth_user");
          sessionStorage.removeItem("auth_user");
          setIsAuthenticated(false);
        }
      }
    };

    verifyToken();
  }, [apiBase]);

  /**
   * 로그인 처리
   * - 백엔드에서 받은 access / refresh 토큰을 저장
   * - axiosInstance가 이후부터 자동으로 Authorization 헤더를 붙여줌
   * - 유저 정보(auth_user)는 LoginPage/SignupPage에서 따로 저장
   * - rememberMe: true면 localStorage, false면 sessionStorage 사용
   */
  const login = (access: string, refresh: string, rememberMe: boolean = true) => {
    setTokens(access, refresh, rememberMe); // 스토리지에 토큰 저장
    setIsAuthenticated(true);
  };

  /**
   * 로그아웃 처리
   * - 서버에 refresh 토큰 블랙리스트 등록 (탈취 토큰 즉시 무효화)
   * - 로컬 토큰 + 유저 정보 삭제
   */
  const logout = () => {
    const refresh = getRefreshToken();
    // 서버 측 토큰 무효화 — 실패해도 로컬 로그아웃은 진행
    if (refresh) {
      api.post(API_ENDPOINTS.AUTH.LOGOUT, { refresh }).catch(() => {});
    }
    clearTokens();
    localStorage.removeItem("auth_user");
    sessionStorage.removeItem("auth_user");
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * AuthContext를 사용하는 훅
 * - 반드시 <AuthProvider> 내부에서만 호출해야 함
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
